import { inject } from '@angular/core';
import {
  CanActivateFn,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Store } from '@ngxs/store';
import { from, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { AUTH_PROVIDER } from '../common/injector-token';
import { AuthSelectors } from '../store/auth/auth.selectors';
import { AuthActions } from '../store/auth/auth.actions';
import { ApplicationActions } from '../store/application/application.actions';
import { ROUTES } from '../common/routes';
import { isStoredTokenValid } from '../util/auth-token.util';
import { NotificationUtil } from '../util/notification.util';
import { UnknownUserTypeError } from '../../domain/auth/model/unknown-user-type.error';

export function isAuthenticatedGuard(): CanActivateFn {
  return (_, state: RouterStateSnapshot) => {
    const store: Store = inject(Store);
    const router: Router = inject(Router);
    const authProvider = inject(AUTH_PROVIDER);
    const notificationUtil = inject(NotificationUtil);
    const token = store.selectSnapshot(AuthSelectors.token);
    const tokenSavedAt = store.selectSnapshot(AuthSelectors.tokenSavedAt);
    const hasValidToken = isStoredTokenValid(token, tokenSavedAt);

    return of(hasValidToken).pipe(
      switchMap((isLogged: boolean) => {
        if (isLogged) {
          return of(true);
        }

        return from(authProvider.refresh()).pipe(
          switchMap(token =>
            store.dispatch(new AuthActions.SaveToken(token)).pipe(
              map(() => true)
            )
          ),
          catchError(() => {
            return store
              .dispatch([
                new ApplicationActions.SetReturnUrl(state.url),
                new AuthActions.ClearAuthState(),
              ])
              .pipe(map(() => false));
          })
        );
      }),
      switchMap((isLogged: boolean) => {
        if (!isLogged) {
          return of(router.parseUrl(ROUTES.AUTH.LOGIN));
        }

        const currentLogged = store.selectSnapshot(
          AuthSelectors.getCurrentLogged
        );
        const currentLoggedLoading = store.selectSnapshot(
          AuthSelectors.currentLoggedLoading
        );
        const attemptedAt = store.selectSnapshot(
          AuthSelectors.currentLoggedAttemptedAt
        );
        const shouldFetch =
          currentLogged == null &&
          !currentLoggedLoading &&
          (attemptedAt == null || Date.now() - attemptedAt > 30_000);

        return shouldFetch
          ? store.dispatch(new AuthActions.GetCurrentLogged()).pipe(
              map(() => true as boolean | UrlTree),
              catchError(error => {
                if (!(error instanceof UnknownUserTypeError)) {
                  return of(true);
                }
                // A role other than the three is never let in (legacy MANAGER, PATIENT, ...).
                return store.dispatch(new AuthActions.Logout()).pipe(
                  tap(() => notificationUtil.error('ERROR.UNAUTHORIZED')),
                  map(() => router.parseUrl(ROUTES.AUTH.LOGIN))
                );
              })
            )
          : of(true);
      })
    );
  };
}
